import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Link,
  CircularProgress,
} from "@mui/material";
import { getAllNews } from "../../apis/user-apis/NewsApis";

const NewsSection = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const data = await getAllNews();
        if (data.success) {
          setNewsItems(data.articles || []);
        } else {
          setError(data.error || "Failed to fetch news");
        }
      } catch (err) {
        console.error("Error fetching news:", err);
        setError("Unable to connect to the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  if (!loading && newsItems.length === 0) {
    return null;
  }

  return (
    <Box
      className="news-section-root"
      sx={{ bgcolor: "#FFF", py: { xs: 8, md: 10 }, width: "100%" }}
    >
      <Container maxWidth="lg" className="news-container">
        {/* HEADER SECTION */}
        <Box className="news-header" sx={{ mb: 5 }}>
          <Typography
            className="overline-text"
            sx={{
              color: "#C59324",
              fontWeight: 700,
              letterSpacing: "0.2em",
              fontSize: "0.85rem",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            In the News
          </Typography>

          <Typography
            className="headline-main"
            sx={{
              mb: "8px",
              color: "#333",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            What People Are{" "}
            <Box
              component="span"
              sx={{
                color: "#C59324",
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Saying
            </Box>
          </Typography>
        </Box>

        {/* NEWS CARDS FLEX WRAPPER */}
        <Box
          className="news-cards-wrapper"
          sx={{ display: "flex", flexWrap: "wrap", gap: 3, minHeight: "200px" }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                py: 5,
              }}
            >
              <CircularProgress sx={{ color: "#C59324" }} />
            </Box>
          ) : error ? (
            <Box sx={{ width: "100%", textAlign: "center", py: 5 }}>
              <Typography sx={{ color: "#D32F2F" }}>{error}</Typography>
            </Box>
          ) : (
            newsItems.map((item, index) => {
              const pubDate = new Date(item.publicationDate);
              const formattedDate = pubDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              });

              return (
                <Box
                  key={item._id || index}
                  className={`news-card-item-${index}`}
                  sx={{
                    width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.33% - 16px)" },
                    maxWidth: { md: "320px" },
                    borderRadius: "8px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    bgcolor: "#FFF",
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "translateY(-4px)" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* DARK HEADER BAR */}
                  <Box
                    className="card-source-header"
                    sx={{ bgcolor: "#051328", py: 1, px: 2.5 }}
                  >
                    <Typography
                      sx={{
                        color: "#C59324",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.source}
                    </Typography>
                  </Box>

                  {/* CARD CONTENT */}
                  <Box
                    className="card-body"
                    sx={{
                      p: 3,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      className="card-title"
                      sx={{
                        color: "#051328",
                        fontSize: "0.95rem",
                        fontWeight: 800,
                        lineHeight: 1.4,
                        mb: 1.5,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      className="card-excerpt"
                      sx={{
                        color: "#5D6785",
                        fontSize: "0.85rem",
                        lineHeight: 1.6,
                        mb: 1,
                        flexGrow: 1,
                      }}
                    >
                      {item.excerpt}
                    </Typography>

                    <Typography
                      className="card-date"
                      sx={{
                        color: "#5D6785",
                        fontSize: "0.75rem",
                        display: "block",
                        mb: 2.5,
                        opacity: 0.8,
                      }}
                    >
                      {formattedDate}
                    </Typography>

                    <Link
                      className="read-more-link"
                      href={item.articleUrl}
                      target="_blank"
                      sx={{
                        color: "#C59324",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        textDecoration: "none",
                        borderBottom: "1.5px solid transparent",
                        transition: "0.3s",
                        alignSelf: "flex-start",
                        "&:hover": { borderBottomColor: "#C59324" },
                      }}
                    >
                      Read Article →
                    </Link>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default NewsSection;
